/**
 * Stable sorting over filtered indices, with missing values always last.
 */

import { toEpochMs } from '../format';
import { getValue, isMissing } from './flatten';
import type { Row, Schema, SortIndices, SortState } from './types';

type SortKey = { index: number; key: unknown; missing: boolean };

function compareKeys(a: SortKey, b: SortKey, direction: 'asc' | 'desc', type: string): number {
  if (a.missing && b.missing) return 0;
  if (a.missing) return 1;
  if (b.missing) return -1;

  const av = a.key;
  const bv = b.key;

  let cmp = 0;
  if (type === 'number') {
    const an = typeof av === 'number' ? av : Number.NaN;
    const bn = typeof bv === 'number' ? bv : Number.NaN;
    if (Number.isNaN(an) && Number.isNaN(bn)) cmp = 0;
    else if (Number.isNaN(an)) cmp = 1;
    else if (Number.isNaN(bn)) cmp = -1;
    else cmp = an - bn;
  } else if (type === 'date') {
    const ams = toEpochMs(av);
    const bms = toEpochMs(bv);
    if (ams === null && bms === null) cmp = 0;
    else if (ams === null) cmp = 1;
    else if (bms === null) cmp = -1;
    else cmp = ams - bms;
  } else if (type === 'boolean') {
    const ab = av === true ? 1 : 0;
    const bb = bv === true ? 1 : 0;
    cmp = ab - bb;
  } else if (type === 'array') {
    const al = Array.isArray(av) ? av.length : 0;
    const bl = Array.isArray(bv) ? bv.length : 0;
    cmp = al - bl;
  } else {
    const aStr = typeof av === 'string' || typeof av === 'number' || typeof av === 'boolean'
      ? String(av)
      : JSON.stringify(av);
    const bStr = typeof bv === 'string' || typeof bv === 'number' || typeof bv === 'boolean'
      ? String(bv)
      : JSON.stringify(bv);
    cmp = aStr.localeCompare(bStr, undefined, { numeric: true, sensitivity: 'base' });
  }

  return direction === 'asc' ? cmp : -cmp;
}

export const sortIndices: SortIndices = (rows, indices, sort, schema) => {
  if (!sort) return [...indices];

  const col = schema.byId.get(sort.column);
  if (!col) return [...indices];

  const keys: SortKey[] = indices.map((i) => {
    const value = getValue(rows[i], col);
    const missing = isMissing(value);
    return { index: i, key: value, missing };
  });

  keys.sort((a, b) => {
    const cmp = compareKeys(a, b, sort.direction, col.type);
    if (cmp !== 0) return cmp;
    return a.index - b.index;
  });

  return keys.map((k) => k.index);
};
